/**
 * SkillMatrixPage — Skills Catalogue & Competency Matrix
 * Tabs: My Skills | Matrix (HR) | Manage Skills (HR)
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal,
    Form, Spinner, Table
} from 'react-bootstrap';
import { Star, Plus, CheckCircle, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];

// Skill level labels and colours
const LEVELS = {
    0: { label:'None',     color:'#e5e7eb', text:'#6b7280' },
    1: { label:'Beginner', color:'#fef3c7', text:'#92400e' },
    2: { label:'Basic',    color:'#dbeafe', text:'#1e40af' },
    3: { label:'Intermediate', color:'#d1fae5', text:'#065f46' },
    4: { label:'Advanced', color:'#ede9fe', text:'#5b21b6' },
    5: { label:'Expert',   color:'#4f46e5', text:'#fff'    },
};

const LevelBadge = ({ level }) => {
    const l = LEVELS[level] || LEVELS[0];
    return (
        <span style={{
            display:'inline-block', padding:'2px 8px', borderRadius:12,
            background: l.color, color: l.text, fontSize:11, fontWeight:600,
        }}>{l.label}</span>
    );
};

const SkillMatrixPage = () => {
    const { user }  = useAuth();
    const isHR      = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState('my');

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Star size={22} className="me-2 text-warning"/>Skills & Competencies
                    </h4>
                    <p className="text-muted small mb-0">Skill matrix · Ratings · Verification</p>
                </div>
            </div>
            <div className="d-flex gap-1 mb-4">
                {[
                    { key:'my',     label:'My Skills' },
                    ...(isHR ? [{ key:'matrix',  label:'Team Matrix' }] : []),
                    ...(isHR ? [{ key:'manage',  label:'Manage Skills' }] : []),
                ].map(t => (
                    <Button key={t.key} size="sm"
                        variant={tab===t.key ? 'warning' : 'outline-secondary'}
                        onClick={() => setTab(t.key)}>
                        {t.label}
                    </Button>
                ))}
            </div>
            {tab === 'my'     && <MySkillsTab isHR={isHR}/>}
            {tab === 'matrix' && isHR && <MatrixTab/>}
            {tab === 'manage' && isHR && <ManageSkillsTab/>}
        </div>
    );
};

// ── My Skills Tab ─────────────────────────────────────────────────────────────
const MySkillsTab = ({ isHR }) => {
    const [mySkills, setMy]   = useState([]);
    const [allSkills, setAll] = useState([]);
    const [loading,  setLoading] = useState(true);
    const [showAdd,  setShowAdd] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/performance/skills/my'),
            axiosInstance.get('/performance/skills'),
        ]).then(([m, a]) => { setMy(m.data.data||[]); setAll(a.data.data||[]); })
          .catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load, []);

    const save = async (skillId, level) => {
        try {
            await axiosInstance.post('/performance/skills/rate', { skill_id: skillId, level });
            toast.success('Skill updated'); load();
        } catch { toast.error('Failed'); }
    };

    // Group by category
    const byCategory = mySkills.reduce((acc, s) => {
        const cat = s.category || 'General';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(s);
        return acc;
    }, {});

    // Skills not yet added
    const myIds = new Set(mySkills.map(s => s.skill_id));
    const unrated = allSkills.filter(s => !myIds.has(s.id));

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="warning"/></div>;

    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <span className="text-muted small">{mySkills.length} skills rated</span>
                <Button size="sm" variant="outline-warning" onClick={() => setShowAdd(true)}>
                    <Plus size={14} className="me-1"/>Add Skill
                </Button>
            </div>

            {mySkills.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <Star size={40} className="mx-auto mb-2 opacity-30"/>
                    <p>No skills rated yet. Add your first skill!</p>
                    <Button size="sm" variant="warning" className="mx-auto"
                        onClick={() => setShowAdd(true)}>
                        <Plus size={14} className="me-1"/>Add Skills
                    </Button>
                </Card>
            ) : (
                Object.entries(byCategory).map(([cat, skills]) => (
                    <Card key={cat} className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white fw-bold border-0 pt-3 pb-2">
                            {cat}
                        </Card.Header>
                        <div className="px-3 pb-3">
                            <Row className="g-2">
                                {skills.map(s => (
                                    <Col key={s.skill_id} xs={12} sm={6} md={4}>
                                        <div className="p-3 rounded-3" style={{ background:'#f8fafc' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fw-medium small">{s.skill_name}</span>
                                                {s.verified && (
                                                    <CheckCircle size={14} className="text-success" title="Verified"/>
                                                )}
                                            </div>
                                            <Form.Select size="sm" value={s.level}
                                                onChange={e => save(s.skill_id, parseInt(e.target.value))}>
                                                {Object.entries(LEVELS).map(([l, info]) => (
                                                    <option key={l} value={l}>{info.label}</option>
                                                ))}
                                            </Form.Select>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Card>
                ))
            )}

            {/* Add skill modal */}
            <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">Add a Skill</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {unrated.length === 0 ? (
                        <p className="text-muted">All available skills have been rated.</p>
                    ) : (
                        <div style={{ maxHeight:400, overflowY:'auto' }}>
                            {unrated.map(s => (
                                <div key={s.id} className="d-flex justify-content-between align-items-center py-2"
                                    style={{ borderBottom:'1px solid #f1f5f9' }}>
                                    <div>
                                        <div className="fw-medium small">{s.name}</div>
                                        <Badge bg="light" text="dark" style={{ fontSize:9 }}>{s.category}</Badge>
                                    </div>
                                    <Button size="sm" variant="outline-warning"
                                        onClick={() => { save(s.id, 1); setShowAdd(false); }}>
                                        Add
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowAdd(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

// ── Team Matrix Tab (HR) ──────────────────────────────────────────────────────
const MatrixTab = () => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterDept, setDept] = useState('');

    useEffect(() => {
        axiosInstance.get('/performance/skills/matrix').then(r => setData(r.data.data)).catch(()=>{}).finally(()=>setLoading(false));
    }, []);

    const verify = async (empId, skillId) => {
        try { await axiosInstance.patch(`/performance/skills/verify/${empId}/${skillId}`); toast.success('Verified ✓'); }
        catch { toast.error('Failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="warning"/></div>;
    if (!data) return null;

    const { skills=[], employees=[], matrix={} } = data;
    const depts = [...new Set(employees.map(e => e.department).filter(Boolean))];
    const filteredEmps = employees.filter(e => !filterDept || e.department === filterDept);

    // Limit columns to first 15 skills for readability
    const displaySkills = skills.slice(0, 15);

    return (
        <>
            <div className="d-flex gap-2 mb-3 flex-wrap">
                <Form.Select size="sm" style={{ maxWidth:200 }} value={filterDept}
                    onChange={e => setDept(e.target.value)}>
                    <option value="">All Departments</option>
                    {depts.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
                {/* Legend */}
                <div className="ms-auto d-flex gap-2 align-items-center flex-wrap" style={{ fontSize:11 }}>
                    {[1,2,3,4,5].map(l => (
                        <span key={l} style={{ background:LEVELS[l].color, color:LEVELS[l].text,
                            padding:'2px 8px', borderRadius:10, fontWeight:600 }}>
                            {l}: {LEVELS[l].label}
                        </span>
                    ))}
                </div>
            </div>
            <div style={{ overflowX:'auto' }}>
                <table style={{ borderCollapse:'separate', borderSpacing:4, minWidth: 200 + displaySkills.length * 90 }}>
                    <thead>
                        <tr>
                            <th style={{ minWidth:160, textAlign:'left', padding:'4px 8px', fontSize:11, color:'#6b7280' }}>Employee</th>
                            {displaySkills.map(s => (
                                <th key={s.id} style={{ minWidth:80, textAlign:'center', padding:'4px 8px', fontSize:11, color:'#6b7280' }}>
                                    <div style={{ maxWidth:80, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                                        title={s.name}>{s.name}</div>
                                    <div style={{ fontSize:9, color:'#9ca3af' }}>{s.category}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEmps.map(emp => (
                            <tr key={emp.id}>
                                <td style={{ padding:'4px 8px', fontSize:12 }}>
                                    <div className="fw-medium">{emp.name}</div>
                                    <div style={{ fontSize:10, color:'#9ca3af' }}>{emp.department}</div>
                                </td>
                                {displaySkills.map(skill => {
                                    const rating = matrix[emp.id]?.[skill.id];
                                    const level  = rating?.level || 0;
                                    const L      = LEVELS[level];
                                    return (
                                        <td key={skill.id} style={{ textAlign:'center', padding:2 }}>
                                            <div
                                                title={`${emp.name} — ${skill.name}: ${L.label}${rating?.verified ? ' ✓' : ''}`}
                                                style={{
                                                    width:72, height:28, borderRadius:6,
                                                    background: L.color,
                                                    color: L.text,
                                                    fontSize:11, fontWeight:600,
                                                    display:'flex', alignItems:'center', justifyContent:'center',
                                                    cursor: level > 0 && !rating?.verified ? 'pointer' : 'default',
                                                    border: rating?.verified ? '2px solid #059669' : '1px solid #e5e7eb',
                                                    margin:'auto',
                                                }}
                                                onClick={() => level > 0 && !rating?.verified && verify(emp.id, skill.id)}>
                                                {level > 0 ? level : '·'}
                                                {rating?.verified && <CheckCircle size={10} style={{ marginLeft:2 }}/>}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-muted mt-2" style={{ fontSize:11 }}>
                Click a cell to verify a skill rating. Green border = verified.
                {skills.length > 15 && ` Showing first 15 of ${skills.length} skills.`}
            </div>
        </>
    );
};

// ── Manage Skills Tab (HR) ────────────────────────────────────────────────────
const ManageSkillsTab = () => {
    const [skills,   setSkills]  = useState([]);
    const [loading,  setLoading] = useState(true);
    const [newName,  setNewName] = useState('');
    const [newCat,   setNewCat]  = useState('General');
    const [saving,   setSaving]  = useState(false);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/skills').then(r => setSkills(r.data.data||[])).catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load, []);

    const add = async () => {
        if (!newName.trim()) return toast.error('Skill name required');
        setSaving(true);
        try { await axiosInstance.post('/performance/skills', { name: newName.trim(), category: newCat }); toast.success('Skill added'); setNewName(''); load(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const del = async (id) => {
        if (!window.confirm('Delete this skill?')) return;
        try { await axiosInstance.delete(`/performance/skills/${id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Failed'); }
    };

    const categories = ['Technical','Soft Skills','Leadership','Domain','Tools','General'];
    const byCat = skills.reduce((acc,s)=>{ (acc[s.category||'General']=acc[s.category||'General']||[]).push(s); return acc; }, {});

    return (
        <>
            {/* Add skill form */}
            <Card className="border-0 shadow-sm mb-4 p-4">
                <div className="fw-bold mb-3">Add New Skill</div>
                <Row className="g-2 align-items-end">
                    <Col xs={5}>
                        <Form.Label className="small fw-bold">Skill Name</Form.Label>
                        <Form.Control size="sm" placeholder="e.g. React.js"
                            value={newName} onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => e.key==='Enter' && add()}/>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Category</Form.Label>
                        <Form.Select size="sm" value={newCat} onChange={e => setNewCat(e.target.value)}>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={3}>
                        <Button size="sm" variant="warning" onClick={add} disabled={saving}>
                            {saving ? <Spinner size="sm"/> : <><Plus size={13} className="me-1"/>Add</>}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {loading ? <div className="text-center py-4"><Spinner animation="border"/></div> : (
                Object.entries(byCat).map(([cat, catSkills]) => (
                    <Card key={cat} className="border-0 shadow-sm mb-3 p-3">
                        <div className="fw-bold small mb-2">{cat}</div>
                        <div className="d-flex flex-wrap gap-2">
                            {catSkills.map(s => (
                                <div key={s.id} className="d-flex align-items-center gap-1"
                                    style={{ background:'#f8fafc', borderRadius:20, padding:'4px 12px', fontSize:13 }}>
                                    <span>{s.name}</span>
                                    <span className="text-muted" style={{ fontSize:11 }}>({s.employee_count||0})</span>
                                    <button style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', padding:'0 2px' }}
                                        onClick={() => del(s.id)}>
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    </Card>
                ))
            )}
        </>
    );
};

export default SkillMatrixPage;
